import { useState } from "react";
import { toast } from "react-hot-toast";

import DataTable from "react-data-table-component";
import Modal from "../Modal";
import TableLoader from "../TableLoader";
import ConfirmPrompt from '../ConfirmPrompt';
import AddAdminForm from "./AddAdminForm";
import AdminUserView from "../AdminUserView";

import useAdminUsers from "../../hooks/useAdminUsers";
import userService from "../../services/userService";

const AdminUserList = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const [isAddAdminModalOpen, setIsAddAdminModalOpen] = useState(false);
  const [isInvitingAdmin, setIsInvitingAdmin] = useState(false);

  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [isDeletingAdmin, setIsDeletingAdmin] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);

  const { data, isLoading, mutate } = useAdminUsers({
    shouldFetch: true,
    page,
    perPage,
    sortField,
    sortOrder,
  });

  const admins = data?.users;
  const adminCount = data?.count;

  const handleSort = async (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 1 : -1);
  };

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleRowClicked = (row) => {
    setSelectedUser(row);
    setIsAdminModalOpen(true);
  };

  const handleInviteAdmin = async (data) => {
    try {
      setIsInvitingAdmin(true);

      const { user } = await userService.inviteAdmin(data);
      await mutate();

      setIsAdminModalOpen(false);

      toast.success(`${user.fullName} invited`, { icon: '🚀' });
    } catch (error) {
      const { message } = error?.response?.data;

      toast.error(message || 'An error occurred while inviting admin');
    } finally {
      setIsInvitingAdmin(false);
    }
  }

  const handleDeleteClicked = (row) => {
    setSelectedUser(row);
    setIsConfirmDeleteModalOpen(true);
  };

  const handleDeleteAdmin = async (userId) => {
    try {
      setIsDeletingAdmin(true);

      await userService.deleteAdmin(userId);
      await mutate();
      setIsConfirmDeleteModalOpen(false);
    } catch (error) {
      const { message } = error?.response?.data;

      toast.error(message || 'An error occurred while deleting admin');
    } finally {
      setIsDeletingAdmin(false);
    }
  };

  const columns = [
    {
      name: 'Name',
      selector: row => row.fullName,
      sortable: true,
      sortField: 'fullName',
    },
    {
      name: '',
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleDeleteClicked(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-red-600 text-white text-nowrap'
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      <div className='flex flex-col'>
        <DataTable
          columns={columns}
          data={admins}
          striped
          highlightOnHover
          pointerOnHover
          progressPending={isLoading}
          progressComponent={<TableLoader />}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={adminCount}
          paginationRowsPerPageOptions={[5, 10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          onRowClicked={handleRowClicked}
        />

        <div className='px-4 pb-4'>
          <button
            className='bg-ase-blue h-[48px] px-4 font-semibold text-white rounded-md flex items-center justify-center w-fit'
            onClick={() => setIsAddAdminModalOpen(true)}
          >
            Add
          </button>
        </div>
      </div>

      <Modal isOpen={isAddAdminModalOpen}>
        <AddAdminForm
          onSubmit={handleInviteAdmin}
          isLoading={isInvitingAdmin}
          onCloseClicked={() => setIsAddAdminModalOpen(false)}
        />
      </Modal>

      <Modal isOpen={isConfirmDeleteModalOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to remove ${selectedUser?.fullName}?`}
          onConfirm={() => handleDeleteAdmin(selectedUser?._id)}
          onCancel={() => setIsConfirmDeleteModalOpen(null)}
          isLoading={isDeletingAdmin}
        />
      </Modal>

      <Modal isOpen={isAdminModalOpen}>
        <AdminUserView
          userId={selectedUser?._id}
          onCloseClicked={() => setIsAdminModalOpen(false)}
        />
      </Modal>
    </>
  )
}

export default AdminUserList;
